/**
 * useDataRow - Central data fetching hook for Data Row blocks
 *
 * Fetches real API data based on entityType + source config.
 * Features:
 * - Per-session cache (no duplicate requests)
 * - Debounced refetch on config change (400ms)
 * - Admin-safe read-only mode (no view increments)
 * - Graceful error fallback (never crashes builder)
 * - Normalizes heterogeneous API responses into uniform card data
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// SESSION CACHE — shared across all useDataRow instances per page load
// ============================================================================
const sessionCache = new Map();
const inflightRequests = new Map();

function getCacheKey(blockType, source, tag, limit) {
  return `${blockType}:${source}:${tag || ''}:${limit}`;
}

// ============================================================================
// ENDPOINT RESOLVER — maps (blockType, source, tag, limit) → API URL
// ============================================================================
function resolveEndpoint(blockType, source, tag, limit) {
  const base = API_URL;
  const lim = limit || 10;

  // --- Series-based blocks ---
  if (['series_carousel', 'trending_series'].includes(blockType)) {
    switch (source) {
      case 'trending': return `${base}/api/homepage/trending?limit=${lim}`;
      case 'new':      return `${base}/api/homepage/new-releases?limit=${lim}`;
      case 'featured': return `${base}/api/featured`;
      case 'tag':      return `${base}/api/series?genres=${encodeURIComponent(tag)}&limit=${lim}&sort=popular`;
      case 'curated':  return `${base}/api/homepage/popular?limit=${lim}`;
      default:         return `${base}/api/homepage/trending?limit=${lim}`;
    }
  }

  // --- Novel blocks ---
  if (blockType === 'novel_carousel') {
    switch (source) {
      case 'trending': return `${base}/api/novels?sort=popular&limit=${lim}`;
      case 'new':      return `${base}/api/novels?sort=newest&limit=${lim}`;
      case 'featured': return `${base}/api/novels?sort=popular&limit=${lim}`;
      case 'tag':      return `${base}/api/novels?genres=${encodeURIComponent(tag)}&limit=${lim}`;
      default:         return `${base}/api/novels?limit=${lim}`;
    }
  }

  // --- Artwork blocks ---
  if (blockType === 'artwork_gallery') {
    switch (source) {
      case 'featured': return `${base}/api/featured-artworks?limit=${lim}`;
      case 'trending': return `${base}/api/art/gallery?sort=popular&limit=${lim}`;
      case 'new':      return `${base}/api/art/gallery?sort=newest&limit=${lim}`;
      default:         return `${base}/api/featured-artworks?limit=${lim}`;
    }
  }

  // --- Creator blocks ---
  if (blockType === 'featured_creators') {
    return `${base}/api/featured-creators?limit=${lim}`;
  }

  // --- New Releases ---
  if (blockType === 'new_releases') {
    return `${base}/api/homepage/new-releases?limit=${lim}`;
  }

  // --- Continue Reading (user-specific, needs auth) ---
  if (blockType === 'continue_reading') {
    return null; // No public endpoint — will fall back to sample
  }

  return `${base}/api/homepage/trending?limit=${lim}`;
}

// ============================================================================
// RESPONSE NORMALIZER — converts varied API shapes to uniform card arrays
// ============================================================================
function normalizeResponse(blockType, raw, limit) {
  let items = [];

  // 1. Extract items array from response
  if (Array.isArray(raw)) {
    items = raw;
  } else if (raw.featured_series) {
    items = raw.featured_series;
  } else if (raw.featured_artworks) {
    items = raw.featured_artworks;
  } else if (raw.featured_creators) {
    items = raw.featured_creators;
  } else if (raw.novels) {
    items = raw.novels;
  } else if (raw.items) {
    items = raw.items;
  } else if (raw.posts) {
    items = raw.posts;
  } else if (raw.trending) {
    items = raw.trending;
  } else if (raw.series) {
    items = raw.series;
  }

  // 2. Normalize per entity type
  if (['series_carousel', 'trending_series', 'new_releases'].includes(blockType)) {
    items = items.map(s => ({
      id: s.id,
      title: s.title || 'Untitled',
      cover_url: s.cover_url || '',
      genre: Array.isArray(s.genres) ? s.genres[0] : (s.genre || ''),
      episodes: s.metrics_cache?.total_episodes || s.episodes || 0,
      views: s.metrics_cache?.total_views || s.views || 0,
    }));
  } else if (blockType === 'novel_carousel') {
    items = items.map(n => ({
      id: n.id,
      title: n.title || 'Untitled',
      cover_url: n.cover_url || '',
      genre: Array.isArray(n.genres) ? n.genres[0] : (n.genre || ''),
      episodes: n.chapter_count || n.metrics_cache?.total_episodes || 0,
      views: n.metrics_cache?.total_views || n.views || 0,
    }));
  } else if (blockType === 'artwork_gallery') {
    items = items.map(a => ({
      id: a.artwork_id || a.id,
      title: a.title || 'Untitled',
      image_url: a.image_url || a.card_url || a.thumb_url || '',
      artist: a.creator_display_name || a.creator_handle || a.artist_name || 'Unknown',
      likes: a.likes || a.metrics?.likes || 0,
    }));
  } else if (blockType === 'featured_creators') {
    items = items.map(c => ({
      id: c.id || c.user_id,
      display_name: c.display_name || c.handle || 'Creator',
      avatar_url: c.avatar_url || c.profile_image || '',
      series_count: c.series_count || 0,
      followers: c.follower_count || c.followers || 0,
    }));
  }

  return items.slice(0, limit);
}

// ============================================================================
// HOOK
// ============================================================================
export default function useDataRow({ blockType, source, tag, limit }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    const cacheKey = getCacheKey(blockType, source, tag, limit);

    // 1. Check session cache
    if (sessionCache.has(cacheKey)) {
      setData(sessionCache.get(cacheKey));
      setLoading(false);
      setError(null);
      return;
    }

    // 2. Deduplicate in-flight requests
    if (inflightRequests.has(cacheKey)) {
      try {
        const result = await inflightRequests.get(cacheKey);
        if (mountedRef.current) {
          setData(result);
          setLoading(false);
          setError(null);
        }
      } catch {
        // handled below
      }
      return;
    }

    // 3. Resolve endpoint
    const endpoint = resolveEndpoint(blockType, source, tag, limit);
    if (!endpoint) {
      // No endpoint (e.g., continue_reading) — return empty, let component show fallback
      setData([]);
      setLoading(false);
      setError('no-endpoint');
      return;
    }

    setLoading(true);
    setError(null);

    // 4. Fetch with dedup
    const fetchPromise = (async () => {
      const res = await fetch(endpoint, {
        headers: { 'X-Preview-Mode': 'true' }, // admin-safe read-only signal
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      return normalizeResponse(blockType, raw, limit);
    })();

    inflightRequests.set(cacheKey, fetchPromise);

    try {
      const items = await fetchPromise;
      sessionCache.set(cacheKey, items);
      if (mountedRef.current) {
        setData(items);
        setLoading(false);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
        setLoading(false);
        // Keep whatever data we had (or empty)
      }
    } finally {
      inflightRequests.delete(cacheKey);
    }
  }, [blockType, source, tag, limit]);

  // Debounced fetch on config change
  useEffect(() => {
    mountedRef.current = true;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchData, 400);
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchData]);

  // Manual refetch (bypasses cache)
  const refetch = useCallback(() => {
    const cacheKey = getCacheKey(blockType, source, tag, limit);
    sessionCache.delete(cacheKey);
    fetchData();
  }, [blockType, source, tag, limit, fetchData]);

  return { data, loading, error, refetch };
}

// Export cache clear for page navigation
export function clearDataRowCache() {
  sessionCache.clear();
}
